import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Copy, X as CloseIcon, CheckIcon } from "lucide-react";
import { Form, InputGroup } from "react-bootstrap";

import { InstagramIcon, LinkInIcon, TwitterIcon } from "assets/icons/index";
import {
  SocialShareIcon,
  StrategyShareModalContainer,
  ImageHeader,
  ModalBodyContent,
  ShareHeading,
  SocialButtonsWrapper,
  LinkInputWrapper,
  CopyButton,
  CloseButtonWrapper,
} from "./StrategyShareModal.styles";

import { contains } from "utils/index";

const SOCIAL_OPTIONS = [
  { key: "instagram", label: "Instagram", Icon: InstagramIcon },
  { key: "linkedin", label: "LinkedIn", Icon: LinkInIcon },
  { key: "x", label: "X", Icon: TwitterIcon },
];

export const StrategyShareModal = ({
  showShareModal,
  toggleShareModal,
  strategyDetail,
}) => {
  const pathname = `/strategy/${strategyDetail?.strategy_id}/detail`;
  const [isCopied, setIsCopied] = useState(false);
  const [url, setUrl] = useState("");

  const strategyName =
    strategyDetail?.name || strategyDetail?.title || "Strategy";
  const coverImage =
    strategyDetail?.coverPhoto || strategyDetail?.strategy_image || null;

  useEffect(() => {
    if (typeof window !== "undefined" && strategyDetail?.strategy_id) {
      let _url = `${window.location.origin}${pathname}`;
      if (!contains(pathname, "/public")) {
        _url += "/public";
      }
      setUrl(_url);
    }
  }, [pathname, strategyDetail?.strategy_id]);

  const socialShareTitle = `Checkout this amazing strategy for investing!`;

  const handleCopyClick = () => {
    navigator.clipboard.writeText(url);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleInstagramShare = () => {
    // If device supports native share sheet (mobile) — use it
    if (navigator.share) {
      navigator.share({
        title: strategyName,
        text: socialShareTitle,
        url,
      });
      return;
    }

    handleCopyClick();
  };

  const handleLinkedInShare = () => {
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
      url
    )}`;
    window.open(linkedInUrl, "_blank", "noopener,noreferrer");
  };

  const handleTwitterShare = () => {
    const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      socialShareTitle
    )}&url=${encodeURIComponent(url)}`;
    window.open(tweetUrl, "_blank", "noopener,noreferrer");
  };

  const SHARE_ACTIONS = {
    instagram: handleInstagramShare,
    linkedin: handleLinkedInShare,
    x: handleTwitterShare,
  };

  return (
    <StrategyShareModalContainer
      show={showShareModal}
      onHide={toggleShareModal}
      className="strategy-share-modal-redesign"
      heading={null}
      showClose={false}
      size="md"
    >
      <ImageHeader coverImage={coverImage}>
        <CloseButtonWrapper onClick={toggleShareModal} data-close-modal="true">
          <CloseIcon size={20} color="white" />
        </CloseButtonWrapper>
        <h2 className="strategy-title">{strategyName}</h2>
      </ImageHeader>

      <ModalBodyContent>
        <ShareHeading>Share this strategy to others</ShareHeading>

        <SocialButtonsWrapper>
          {SOCIAL_OPTIONS.map(({ key, label, Icon }) => (
            <SocialShareIcon key={key} onClick={SHARE_ACTIONS[key]}>
              <Icon />
              <span>{label}</span>
            </SocialShareIcon>
          ))}
        </SocialButtonsWrapper>

        <LinkInputWrapper>
          <InputGroup className="mb-0">
            <InputGroup.Text id="share-link-prefix">http://</InputGroup.Text>
            <Form.Control
              placeholder="www.surmount.ai/strategylink"
              aria-label="Share link"
              aria-describedby="share-link-prefix"
              readOnly
              value={url.replace(/^https?:\/\//, "")}
            />
          </InputGroup>

          <CopyButton onClick={handleCopyClick} disabled={isCopied}>
            {isCopied ? (
              <>
                Copied
                <CheckIcon width={16} height={16} />
              </>
            ) : (
              <>
                Copy <Copy size={16} stroke="white" />
              </>
            )}
          </CopyButton>
        </LinkInputWrapper>
      </ModalBodyContent>
    </StrategyShareModalContainer>
  );
};

StrategyShareModal.propTypes = {
  showShareModal: PropTypes.bool,
  toggleShareModal: PropTypes.func,
  strategyDetail: PropTypes.shape({
    strategy_id: PropTypes.string,
    name: PropTypes.string,
    title: PropTypes.string,
    coverPhoto: PropTypes.string,
    strategy_image: PropTypes.string,
  }),
};
